import { getData } from '../../_helpers/getData';
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import Navbar from './../../components/Navbar'
import { getChatToken, propertyService, updateUserResource, userService, ensureChatChannel } from './../../_services/index'
import constant from '../../_config/constant'
import Conversation from './Conversation'
import ChatList from './ChatList'
import { Client } from '@twilio/conversations'
import 'react-toastify/dist/ReactToastify.css'
import { ToastContainer } from 'react-toastify'
import TopBar from './../../components/topbar'
import Modal from '../ReactModal'
import { checkPhoneNumberOrKeywords } from './forbiddenWords'

function chatSetupFailure(error) {
    const status = Number(error?.response?.status || error?.status || error?.statusCode)
    const serverMessage = error?.response?.data?.message
    return {
        message: typeof serverMessage === 'string' && serverMessage.trim()
            ? serverMessage.trim().slice(0, 500)
            : 'Chat setup could not be completed. Please retry.',
        retryable: !status || status === 429 || status >= 500,
    }
}

function Messages() {
    const history = useHistory()
    const client = useRef(null)
    const chatRef = useRef(null)

    const user = getData()
    const reservationId =
        (history && history.location && history.location.state && history.location.state.property && history.location.state.property.reservationId) ||
        ''
    const initialChannelId = (history && history.location && history.location.state && history.location.state.channelId) || ''

    const channelIdRef = useRef(initialChannelId)
    const channelListenersRef = useRef(new Set())
    const [initialLoading, setInitialLoading] = useState(true)
    const [channels, setChannels] = useState([])
    const [activeChannelId, setActiveChannelId] = useState(initialChannelId)
    const [messages, setMessages] = useState([])
    const [error, setError] = useState(null)
    const retrySetupRef = useRef(null)
    const retryInFlightRef = useRef(false)
    const [retryingSetup, setRetryingSetup] = useState(false)

    const recordSetupFailure = (failure, retry) => {
        const detail = chatSetupFailure(failure)
        retrySetupRef.current = detail.retryable ? retry : null
        setError(detail.message)
    }

    const retryChatSetup = async () => {
        const retry = retrySetupRef.current
        if (!retry || retryInFlightRef.current) return
        const activeClient = client.current
        retryInFlightRef.current = true
        setRetryingSetup(true)
        try {
            await retry()
            if (client.current !== activeClient) return
            retrySetupRef.current = null
            setError(null)
        } catch (failure) {
            if (client.current === activeClient) recordSetupFailure(failure, retry)
        } finally {
            retryInFlightRef.current = false
            if (client.current === activeClient) setRetryingSetup(false)
        }
    }

    const [showMobileConversation, setShowMobileConversation] = useState(false)
    const [text, setText] = useState('')

    const [guestId, setGuestId] = useState(null)

    const scrollToBottom = () => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight
        }
    }

    // Attach the messageAdded listener only once per channel. Without this guard a channel
    // can accumulate multiple listeners (init loop + conversationJoined + new-channel), which
    // makes each incoming message render twice (the optimistic reconcile only consumes one).
    const addMessageListenerIfNotExists = channel => {
        try {
            const sid = channel && (channel.sid || channel.channelSid || channel.uniqueName)
            if (!sid) return
            if (!channelListenersRef.current.has(sid)) {
                channel.on('messageAdded', message => handleIncomingMessage(channel.sid, message))
                channelListenersRef.current.add(sid)
            }
        } catch (e) {
            console.error('Failed to add message listener:', e)
        }
    }

    // Helper to decode a JWT (safe, for debug only)
    const parseJwt = token => {
        try {
            const payload = token.split('.')[1]
            const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
            return JSON.parse(decodeURIComponent(escape(json)))
        } catch (e) {
            return null
        }
    }

    // Refresh token from backend and update or recreate Twilio client
    const refreshTokenAndUpdateClient = async () => {
        try {
            const refreshed = await getChatToken(user.id)
            const newToken = refreshed?.data?.data?.token
            if (!newToken) throw new Error('No token returned from backend')

            if (client.current) {
                await client.current.updateToken(newToken)
                console.log('Twilio client token updated')
            } else {
                client.current = new Client(newToken)
                console.log('Twilio client recreated with refreshed token')
            }

            const info = parseJwt(newToken)
            console.log('Decoded token identity:', info?.identity)
            return true
        } catch (err) {
            console.error('Failed to refresh token and update client:', err)
            return false
        }
    }

    useEffect(() => {
        if (!initialLoading) {
            scrollToBottom()
        }
    }, [messages, initialLoading])

    const handleIncomingMessage = useCallback(
        (channelSid, newMessage) => {
            console.log('Incoming message event for channel', channelSid, 'message:', newMessage.body, 'at', new Date().toISOString())
            if (channelSid === channelIdRef.current) {
                setMessages(prevMessages => {
                    const incomingText = newMessage.body
                    const incomingAuthor = newMessage.author
                    const incomingTime = new Date(newMessage.dateCreated).getTime()

                    // Try to find a pending optimistic message that matches author & text within 15s
                    const pendingIndex = prevMessages.findIndex(m => m.pending && m.author == incomingAuthor && m.text == incomingText && Math.abs(new Date(m.timestamp).getTime() - incomingTime) < 15000)

                    if (pendingIndex !== -1) {
                        // Replace the optimistic message with the confirmed server message
                        const updated = [...prevMessages]
                        updated[pendingIndex] = {
                            text: incomingText,
                            author: incomingAuthor,
                            type: incomingAuthor == user.id ? 'sent' : 'received',
                            timestamp: new Date(incomingTime),
                        }
                        return updated
                    }

                    // Otherwise append normally
                    return [
                        ...prevMessages,
                        {
                            text: incomingText,
                            author: incomingAuthor,
                            type: incomingAuthor == user.id ? 'sent' : 'received',
                            timestamp: new Date(incomingTime),
                        },
                    ]
                })
                scrollToBottom()
            }

            setChannels(prevChannels => {
                const updatedChannels = [...prevChannels]
                const index = updatedChannels.findIndex(c => c.channel.sid === channelSid)

                if (index !== -1) {
                    const [updatedChannel] = updatedChannels.splice(index, 1)
                    updatedChannel.lastMessage = newMessage.body
                    updatedChannel.lastUpdated = new Date().toString()

                    if (channelSid != channelIdRef.current) {
                        updatedChannel.unreadMessagesCount = (updatedChannel.unreadMessagesCount || 0) + 1
                    }

                    updatedChannels.unshift(updatedChannel)
                }

                return updatedChannels.sort((a, b) => b.lastUpdated - a.lastUpdated)
            })
        },
        [user.id]
    )

    const fetchChannelMessages = async channelSid => {
        console.log('Fetching messages for channel SID:', channelSid)
        try {
            setMessages([])
            if (!client.current) {
                console.warn('Twilio client is not initialized.')
                return
            }

            // helper: get channel with a retry for transient Conflict errors
            const getChannelWithRetry = async (sid, attempts = 2) => {
                try {
                    return await client.current.getConversationBySid(sid)
                } catch (e) {
                    const msg = (e && (e.message || e.toString())) || ''
                    if ((msg.includes('Conflict') || msg.toLowerCase().includes('conflict')) && attempts > 0) {
                        console.warn('Transient Conflict fetching channel, retrying...', { sid })
                        await new Promise(res => setTimeout(res, 500))
                        return getChannelWithRetry(sid, attempts - 1)
                    }
                    throw e
                }
            }

            let targetChannel
            try {
                targetChannel = await getChannelWithRetry(channelSid)
            } catch (err) {
                const errStr = (err && (err.message || err.toString())) || ''
                // If Forbidden from Twilsock, try refreshing token once and retry
                if (errStr.includes('Forbidden') || errStr.includes('TwilsockUpstreamError')) {
                    console.warn('Forbidden error detected when fetching channel. Attempting token refresh and retry...')
                    const refreshed = await refreshTokenAndUpdateClient()
                    if (refreshed) {
                        targetChannel = await getChannelWithRetry(channelSid)
                    } else {
                        throw err
                    }
                } else {
                    throw err
                }
            }
            console.log('Channel fetched, status:', targetChannel?.state?.status || 'unknown')
            if (targetChannel && targetChannel.status !== 'joined') {
                try {
                    await targetChannel.join()
                    console.log('Joined channel')
                } catch (err) {
                    console.log('Join failed (maybe already joined):', err)
                }
            }

            if (targetChannel) {
                let bookingId = targetChannel?.attributes?.property?.reservationId
                let booking = undefined
                if(bookingId) {
                    booking = await propertyService.bookingDetailByBookingId(bookingId, true);
                }
                const participants = await targetChannel.getParticipants()
                const memberPromises = participants.map((p) => p.identity).map(async (id) => {
                    return await client.current.getUser(id)
                })

                const membersInfo = await Promise.all(memberPromises)

                const usersMap = membersInfo.reduce((map, obj) => {
                
                    map.set(obj.identity, obj);
                    return map;
                  }, new Map());

                let currentUserInfo = usersMap.get(`${user.id}`)

                if(!currentUserInfo || !currentUserInfo.attributes["name"] || !currentUserInfo.attributes['profile']) {
                    const data = currentUserInfo ? {
                        ...currentUserInfo,
                        id: user.id,
                        name: user.name,
                        profile: user.avatar,
                        email: user.personalEmail,
                    } : {
                        id: user.id,
                        name: user.name,
                        profile: user.avatar,
                        email: user.personalEmail,
                    } 
                    await updateUserResource(user.id, data);
                    usersMap.set(`${user.id}`, data)
                    currentUserInfo = data
                }
               
                setGuestId(undefined)
                let guestId = targetChannel?.attributes?.guestId
                if(!guestId) {
                    const guests = participants.filter((p) => {
                        return p.identity != user.id
                    }).map((p) => ({
                        id: p.identity
                    }))
                    
                    if(guests.length == 1) {
                        guestId = guests[0].id
                    } else {
                        if(booking?.data?.length > 0) {
                            guestId = booking.data[0].userId
                        }
                    }
                }
                setGuestId(guestId) 
                let guestUserInfo = usersMap.get(`${guestId}`)
                
                if(!guestUserInfo || !guestUserInfo.attributes["name"] || !guestUserInfo.attributes['profile']) {
                    if(bookingId && booking) {
                        const userMeta = booking?.data?.length > 0 ? booking.data[0].userMeta: undefined

                        const data = guestUserInfo ? {
                            ...guestUserInfo,
                            id: guestId,
                            name: userMeta?.user.name,
                            profile: userMeta?.user.avatar,
                        } : {
                            id: guestId,
                            name: userMeta?.user.name,
                            profile: userMeta?.user.avatar,
                        }
                        usersMap.set(`${guestId}`, data)
                        guestUserInfo = data
                    }
                }

                let propertyData = {...targetChannel.attributes.property}

                if(currentUserInfo && currentUserInfo.attributes) {
                    if(currentUserInfo.attributes.name && propertyData.host != currentUserInfo.attributes.name) {
                       propertyData.host = currentUserInfo.attributes.name
                   }

                   if(currentUserInfo.attributes.profile && propertyData.hostAvatar != currentUserInfo.attributes.profile) {
                       propertyData.hostAvatar = currentUserInfo.attributes.profile
                   }
                }
                
                if(guestUserInfo && guestUserInfo.attributes) {
                    if(guestUserInfo.attributes.name && propertyData.user != guestUserInfo.attributes.name) {
                        propertyData.user = guestUserInfo.attributes.name
                    }

                    if(guestUserInfo.attributes.profile && propertyData.userAvatar != guestUserInfo.attributes.profile) {
                        propertyData.userAvatar = guestUserInfo.attributes.profile
                    }
                }

                targetChannel = await targetChannel.updateAttributes({
                    ...targetChannel?.attributes,
                    hostId: user.id,
                    guestId: guestId ?? targetChannel?.attributes?.guestId,
                    property: propertyData
                })

                const messages = await targetChannel.getMessages()
                const formattedMessages = messages.items.map(msg => ({
                    text: msg.body,
                    author: msg.author,
                    type: msg.author == user.id ? 'sent' : 'received',
                    timestamp: new Date(msg.dateCreated),
                }))

                setMessages(formattedMessages)               
                scrollToBottom()
            } else {
                console.warn('Target channel not found.')
            }
        } catch (err) {
            console.error('TEST Error fetching channel messages:', err)

            const errStr = (err && (err.message || err.toString())) || ''
            // If Forbidden from Twilsock, try refreshing token once and retry
            if (errStr.includes('Forbidden') || errStr.includes('TwilsockUpstreamError')) {
                console.warn('Forbidden error detected from Twilio. Attempting token refresh and retry...')
                const refreshed = await refreshTokenAndUpdateClient()
                if (refreshed) {
                    try {
                        const retriedChannel = await client.current.getConversationBySid(channelSid)
                        if (retriedChannel) {
                            const messages = await retriedChannel.getMessages()
                            const formattedMessages = messages.items.map(msg => ({
                                text: msg.body,
                                author: msg.author,
                                type: msg.author == user.id ? 'sent' : 'received',
                                timestamp: new Date(msg.dateCreated),
                            }))
                            setMessages(formattedMessages)
                            scrollToBottom()
                            return
                        }
                    } catch (retryErr) {
                        console.error('Retry after token refresh failed:', retryErr)
                    }
                }
            }
        }
    }
    const fetchChatData = useCallback(async () => {
        console.log('Initializing chat data...')
        console.log('User ID:', user.id)
         if (client.current) return

         try {
             setInitialLoading(true)
             const tokenResponse = await getChatToken(user.id)
            const initialToken = tokenResponse?.data?.data?.token
            if (!initialToken) {
                console.error('Failed to initialize chat: no chat token was returned.')
                setError('Failed to initialize chat. Please try again later.')
                setInitialLoading(false)
                return
            }
            const info = parseJwt(initialToken)
            console.log('Token identity at initialization:', info?.identity)
            if (info && info.identity && String(info.identity) !== String(user.id)) {
                console.warn('Token identity does not match local user id. This often causes Forbidden errors.')
            }
             const twilioClient = new Client(initialToken)
             client.current = twilioClient

            twilioClient.on('connectionStateChanged', state => {
                if (state === 'disconnected' || state === 'denied') {
                    console.warn('Connection lost, attempting to reconnect...')
                }
            })

            twilioClient.on('tokenAboutToExpire', async () => {
                try {
                    const refreshedToken = await getChatToken(user.id)
                    await twilioClient.updateToken(refreshedToken.data.data.token)
                } catch (err) {
                    console.error('Failed to refresh token:', err)
                    setError('Token refresh failed!')
                }
            })

            twilioClient.on('conversationJoined', async channel => {
                console.log('Joined conversation:', channel.sid)
                addMessageListenerIfNotExists(channel)
                setChannels(prev => [
                    {
                        channel: channel,
                        lastMessage: '',
                        unreadCount: 0,
                        lastUpdated: new Date(),
                    },
                    ...prev.filter(item => item.channel.sid !== channel.sid),
                ])
            })

            const subscribedChannels = await twilioClient.getSubscribedConversations()
            const channelDataList = []

            let currentPage = subscribedChannels
            while (currentPage) {
                const pageData = await Promise.all(
                    currentPage.items.map(async channel => {
                        addMessageListenerIfNotExists(channel)
                        let unreadMessagesCount = ''
                        try {
                            unreadMessagesCount = (await channel.getUnreadMessagesCount()) || ''
                        } catch (e) {
                            // Twilio 404s when no read horizon is set yet for this user — treat as zero.
                            unreadMessagesCount = ''
                        }

                        const messages = await channel.getMessages()
                        const lastMessage = messages.items[messages.items.length - 1] ? messages.items[messages.items.length - 1].body : ''
                        const lastUpdated = lastMessage ? new Date(messages.items[messages.items.length - 1].dateCreated).toString() : 0

                        return {
                            channel,
                            lastMessage,
                            lastCreated: channel.dateCreated,
                            unreadMessagesCount,
                            lastUpdated,
                        }
                    })
                )
                channelDataList.push(...pageData)
                currentPage = currentPage.hasNextPage ? await currentPage.nextPage() : null
            }

            setChannels(
                channelDataList.sort((a, b) => {
                    const dateA = new Date(a.lastUpdated || a.lastCreated).getTime()
                    const dateB = new Date(b.lastUpdated || b.lastCreated).getTime()
                    return dateB - dateA
                })
            )

            const selectChannel = async (selectedChannel) => {
                setActiveChannelId(selectedChannel.channel.sid)
                setChannels(prev => {
                    const updatedChannels = [
                        selectedChannel, // Add the default channel at the top
                        ...prev.filter(c => c.channel.sid !== selectedChannel.channel.sid), // Filter out the old instance of the default channel
                    ].sort((a, b) => {
                        const dateA = new Date(a.lastUpdated || a.lastCreated).getTime()
                        const dateB = new Date(b.lastUpdated || b.lastCreated).getTime()
                        return dateB - dateA
                    })

                    return updatedChannels
                })

                channelIdRef.current = selectedChannel.channel.sid
                console.log('Default channel set to SID:', selectedChannel.channel.sid)
                await fetchChannelMessages(selectedChannel.channel.sid)
            }

            let defaultChannel =
                channelDataList.find(c => c.channel.attributes.property && c.channel.attributes.property.reservationId === reservationId) ||
                channelDataList.find(c => c.channel.sid === initialChannelId)

            if (!defaultChannel && reservationId) {
                const requestedReferences = { propertyId: history.location.state.property?.id, reservationId }
                const setupRequestedChannel = async () => {
                    const ensureResponse = await ensureChatChannel(requestedReferences)
                    const channelSid = ensureResponse?.data?.data?.sid
                    if (!channelSid) throw new Error('Backend did not return a channel sid')
                    if (client.current !== twilioClient) return
                    const newChannel = await twilioClient.getConversationBySid(channelSid)
                    if (client.current !== twilioClient) return
                    await updateUserResource(user.id, {
                        id: user.id,
                        name: user.name,
                        profile: user.avatar,
                        email: user.personalEmail,
                    })
                    if (client.current !== twilioClient) return
                    addMessageListenerIfNotExists(newChannel)
                    await selectChannel({
                        channel: newChannel,
                        lastMessage: '',
                        unreadCount: 0,
                        lastUpdated: newChannel.dateUpdated,
                        lastCreated: newChannel.dateCreated,
                    })
                }
                try {
                    await setupRequestedChannel()
                } catch (failure) {
                    if (client.current === twilioClient) recordSetupFailure(failure, setupRequestedChannel)
                }
                // A failed requested chat must not fall back to an unrelated conversation.
                return
            }

            if (!defaultChannel && channelDataList.length > 0) defaultChannel = channelDataList[0]
            if (defaultChannel) await selectChannel(defaultChannel)
         } catch (err) {
             console.error('Failed to initialize chat:', err)
             setError('Failed to initialize chat. Please try again later.')
             client.current = null
         } finally {
             setInitialLoading(false)
         }
     }, [user.id, reservationId, initialChannelId, handleIncomingMessage])

    useEffect(() => {
        if (user.id) {
            fetchChatData()
        }
        return () => {
            if (client.current) {
                client.current.shutdown()
                client.current = null
            }
            retrySetupRef.current = null
            retryInFlightRef.current = false
            channelListenersRef.current.clear()
        }
    }, [fetchChatData, user.id])

    useEffect(() => {
        if (activeChannelId && client.current) {
            console.log('Active channel changed, fetching messages for SID:', activeChannelId)
            fetchChannelMessages(activeChannelId)
        }
    }, [activeChannelId])

    const handleChannelClick = async channelSid => {

        console.log('Fetching messages for channel SID chanel click:', channelSid);
        if (!client.current) {
            console.warn('Twilio client is not initialized.')
            return
        }

        // if (channelSid === activeChannelId) return
        setText('')
        setActiveChannelId(channelSid)
        const channelSidFetched = await client.current.getConversationBySid(channelSid)
        await channelSidFetched.setAllMessagesRead()
        channelIdRef.current = channelSid

        setChannels(prevChannels => prevChannels.map(c => (c.channel.sid === channelSid ? { ...c, unreadMessagesCount: 0 } : c)))
        setShowMobileConversation(true)
        //await fetchChannelMessages(channelSid)
    }

    const sendMessage = async message => {
        if (!client.current || !activeChannelId) {
            console.warn('Twilio client or active channel is not initialized.')
            return
        }

        try {
            const channel = await client.current.getConversationBySid(activeChannelId)
            if (channel) {
                if (channel.status !== 'joined') {
                    await channel.join()
                }

                if (checkPhoneNumberOrKeywords(message)) {
                    return alert(
                        '⚠️ For your safety and to protect our community, Crewmates does not allow the exchange of personal contact information or off-platform payments. Please keep all communication and transactions within the platform. Continued violations may result in account suspension.'
                    )
                }

                // Optimistically update UI so the sent message appears instantly.
                // Mark as pending so we can reconcile when the server event arrives and avoid duplicates.
                const optimisticMessage = { text: message, author: user.id, type: 'sent', timestamp: new Date(), pending: true }
                setMessages(prev => [...prev, optimisticMessage])
                scrollToBottom()

                await channel.sendMessage(message)

                setChannels(prevChannels => {
                    const updatedChannels = [...prevChannels]
                    const index = updatedChannels.findIndex(c => c.channel.sid === activeChannelId)

                    if (index !== -1) {
                        const [updatedChannel] = updatedChannels.splice(index, 1)
                        updatedChannel.lastMessage = message
                        updatedChannel.lastUpdated = new Date().toString()
                        updatedChannels.unshift(updatedChannel)
                    }

                    return updatedChannels
                })
                const data = { message: message, guestID: [guestId ?? user.id], property: channel.property, channelId: activeChannelId }
                await userService.sendChatMessageToGuest({ data })
            } else {
                console.warn('Channel not found for SID:', activeChannelId)
            }
        } catch (err) {
            console.error('Failed to send message:', err)
        }
    }

    return (
        <div id="main-wrapper">
            <ToastContainer />
            <TopBar user={user} showSearch={false} title="Messages" />
            <div className="deznav">
                <div className="deznav-scroll">
                    <Navbar />
                </div>
            </div>
            {error && <div role="alert" className="tw-text-sm tw-text-center tw-text-gray-600 tw-my-12">
                <p>{error}</p>
                {retrySetupRef.current && <button type="button" disabled={retryingSetup} onClick={retryChatSetup}>
                    {retryingSetup ? 'Retrying...' : 'Retry'}
                </button>}
            </div>}
            {initialLoading && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                    }}
                >
                    <div
                        style={{
                            width: '50px',
                            height: '50px',
                            border: '5px solid #f3f3f3',
                            borderTop: '5px solid #1e3351',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                        }}
                    />
                    <style>
                        {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}
                    </style>
                </div>
            )}

            {!error && !initialLoading && (
                <div className="content-body no-min-height">
                    <div className="main-container tw-relative tw-mx-4 tw-overflow-hidden">
                        <ChatList
                            activeChannel={channels && channels.find(c => c.channel.sid === activeChannelId)}
                            client={client}
                            channelList={channels}
                            handleChannelClick={handleChannelClick}
                            showMobileConversation={showMobileConversation}
                        />
                        <Conversation
                            channelDetails={channels.find(c => c.channel.sid === activeChannelId)}
                            client={client}
                            userId={user.id}
                            chatRef={chatRef}
                            initialLoading={initialLoading}
                            sendMessage={sendMessage}
                            messages={messages}
                            hostProfileImage={user.avatar}
                            showMobileConversation={showMobileConversation}
                            setShowMobileConversation={setShowMobileConversation}
                            text={text}
                            setText={setText}
                            setActiveChannelId={setActiveChannelId}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default Messages
